/**
 * The SEON JS agent's device session, for fraud screening of sign-ups and checkouts. boxed
 * parity (`lib/fraud.ts` there): the agent is a script tag, `window.seon.init()` once, then
 * `getSession(config)` per request. Loaded only when the tenant's storefront has screening on,
 * so stores that do not pay for SEON never fetch a third-party script. Never throws and never
 * blocks the shopper: a missing or slow session simply means SEON scores without a device.
 */

interface SeonConfig {
  geolocation: { canPrompt: boolean };
  dnsResolverDomain?: string;
  networkTimeoutMs: number;
  fieldTimeoutMs: number;
  region: string;
  silentMode: boolean;
}

interface SeonSdk {
  init: () => void;
  getSession: (config: SeonConfig) => Promise<string>;
}

declare global {
  interface Window {
    seon?: SeonSdk;
  }
}

/** SEON's public agent host; a stage may point at its own resolver domain instead. */
const AGENT_DOMAIN =
  (import.meta.env as Record<string, string | undefined>).VITE_SEON_AGENT_DOMAIN ??
  "cdn.seondf.com";
const AGENT_URL = `https://${AGENT_DOMAIN}/js/v6/agent.umd.js`;

const CONFIG: SeonConfig = {
  geolocation: { canPrompt: false },
  networkTimeoutMs: 2000,
  fieldTimeoutMs: 2000,
  region: "us",
  silentMode: true,
};

/** Past this the request goes out without a session; the shopper never waits on SEON. */
const SESSION_BUDGET_MS = 3000;

let loading: Promise<SeonSdk | null> | null = null;
let cached: string | null = null;

function loadAgent(): Promise<SeonSdk | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.seon) return Promise.resolve(window.seon);
  loading ??= new Promise<SeonSdk | null>((resolve) => {
    const script = document.createElement("script");
    // Fraud screening never waits on a cookie banner: Termly's autoblocker reads the category the
    // moment `src` is assigned, so it is set first.
    script.dataset.categories = "essential";
    script.src = AGENT_URL;
    script.async = true;
    script.onload = () => {
      try {
        window.seon?.init();
      } catch {
        // The agent refusing to init is SEON's problem, not the shopper's.
      }
      resolve(window.seon ?? null);
    };
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loading;
}

/** Starts loading the agent so the first session is ready by the time the shopper submits. */
export function prepareDeviceSession(): void {
  void loadAgent();
}

/** The device session for the next request, or null when the agent is unavailable or slow. */
export async function getDeviceSession(): Promise<string | null> {
  if (cached) return cached;
  try {
    const session = await Promise.race([
      loadAgent().then((seon) => (seon ? seon.getSession(CONFIG) : null)),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SESSION_BUDGET_MS)),
    ]);
    if (session) cached = session;
    return session;
  } catch {
    return null;
  }
}

/** Header carrying the session to the BFF, which forwards it to the platform verbatim. */
export const DEVICE_SESSION_HEADER = "x-loot-device-session";

/** `{ "x-loot-device-session": … }` when a session is available, else `{}`. */
export async function deviceSessionHeaders(enabled: boolean): Promise<Record<string, string>> {
  if (!enabled) return {};
  const session = await getDeviceSession();
  return session ? { [DEVICE_SESSION_HEADER]: session } : {};
}
